// Archivo: src/services/admin.service.js

const API_URL = process.env.REACT_APP_API_URL || '';
const STORAGE_URL = process.env.REACT_APP_STORAGE_URL;

/**
 * Obtiene el token de autorización del localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Configuración de headers para peticiones JSON autenticadas
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
};

/**
 * Configuración de headers para peticiones con archivos
 */
const getFileUploadHeaders = () => {
  const token = getAuthToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/json'
    // No incluir Content-Type para multipart/form-data
  };
};

/**
 * Maneja las respuestas de la API con interceptor de errores de autenticación
 */
const handleApiResponse = async (response) => {
  if (!response.ok) {
    // Si es error 401 (no autorizado) o 403 (prohibido), limpiar sesión
    if (response.status === 401 || response.status === 403) {
      console.warn('Sesión expirada o sin permisos, limpiando localStorage');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      // Redirigir al login
      window.location.href = '/login';
      throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
    }
    
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Error ${response.status} en la respuesta del servidor.`);
  }
  return response.json();
};

/**
 * Verifica si hay token antes de hacer peticiones autenticadas
 */
const ensureAuthenticated = () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación. Por favor, inicia sesión.');
  }
  return token;
};

// =================== SERVICIOS ===================

/**
 * Obtiene todos los servicios (para administración)
 */
export const getAllServices = async () => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/services`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const data = await handleApiResponse(response);
    
    // Add null check to prevent map errors
    if (!data || !data.data) {
      console.error('Invalid response format:', data);
      return [];
    }
    
    return data.data.map(service => ({
      ...service,
      logo_url: service.logo_path ? `${STORAGE_URL}/${service.logo_path}` : null,
    }));
  } catch (error) {
    console.error('Error getting all services:', error);
    throw error;
  }
};

/**
 * Obtiene un servicio específico por ID (para administración)
 * @param {number} serviceId - ID del servicio
 */
export const getServiceById = async (serviceId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/services/${serviceId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting service by ID:', error);
    throw error;
  }
};

/**
 * Crea un nuevo servicio
 * @param {FormData} formData - Datos del servicio (con posible archivo)
 */
export const createService = async (formData) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/services`, {
      method: 'POST',
      headers: getFileUploadHeaders(),
      body: formData
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error creating service:', error);
    throw error;
  }
};

/**
 * Actualiza un servicio existente
 * @param {number} serviceId - ID del servicio
 * @param {FormData} formData - Datos del servicio (con posible archivo)
 */
export const updateService = async (serviceId, formData) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/services/${serviceId}/update`, {
      method: 'POST',
      headers: getFileUploadHeaders(),
      body: formData
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error updating service:', error);
    throw error;
  }
};

/**
 * Elimina un servicio (soft delete o permanente)
 * @param {number} serviceId - ID del servicio
 * @param {boolean} force - true para eliminación permanente, false para soft delete
 */
export const deleteService = async (serviceId, force = false) => {
  try {
    ensureAuthenticated();
    
    // El endpoint base para eliminar (soft delete) es DELETE /services/{id}
    // No incluimos body por compatibilidad con APIs que esperen solo el método.
    const response = await fetch(`${API_URL}/services/${serviceId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error deleting service:', error);
    throw error;
  }
};

/**
 * Restaura un servicio que fue eliminado (soft delete)
 * @param {number} serviceId - ID del servicio a restaurar
 */
export const restoreService = async (serviceId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/services/${serviceId}/restore`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error restoring service:', error);
    throw error;
  }
};

/**
 * Elimina permanentemente un servicio
 * @param {number} serviceId - ID del servicio
 */
export const destroyServicePermanently = async (serviceId) => {
  try {
    ensureAuthenticated();

    const response = await fetch(`${API_URL}/services/${serviceId}/destroyPermanently`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error destroying service permanently:', error);
    throw error;
  }
};

// =================== SECCIONES ===================

/**
 * Obtiene todas las secciones de un servicio (incluyendo eliminadas)
 * @param {number} serviceId - ID del servicio
 */
export const getServiceSections = async (serviceId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/services/${serviceId}/sections`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting service sections:', error);
    throw error;
  }
};

/**
 * Obtiene una sección específica por ID (para administración)
 * @param {number} sectionId - ID de la sección
 */
export const getSectionById = async (sectionId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/sections/${sectionId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting section by ID:', error);
    throw error;
  }
};

/**
 * Crea una nueva sección
 * @param {Object} sectionData - Datos de la sección
 */
export const createSection = async (sectionData) => {
  try {
    ensureAuthenticated();
    
    console.log('Datos enviados al crear sección:', sectionData);
    
    // Si hay archivos, usar FormData
    if (sectionData.documents && sectionData.documents.length > 0) {
      const formData = new FormData();
      formData.append('title', sectionData.title);
      formData.append('description', sectionData.description);
      formData.append('service_id', sectionData.service_id);
      formData.append('user_id', sectionData.user_id);
      
      // Agregar URLs como array
      if (sectionData.urls && sectionData.urls.length > 0) {
        sectionData.urls.forEach((url, index) => {
          if (url.trim()) {
            formData.append(`urls[${index}]`, url);
          }
        });
      }
      
      // Agregar documentos
      sectionData.documents.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });

      const response = await fetch(`${API_URL}/sections`, {
        method: 'POST',
        headers: getFileUploadHeaders(),
        body: formData
      });
      
      return await handleApiResponse(response);
    } else {
      // Si no hay archivos, usar JSON
      const response = await fetch(`${API_URL}/sections`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(sectionData)
      });
      
      return await handleApiResponse(response);
    }
  } catch (error) {
    console.error('Error creating section:', error);
    throw error;
  }
};

/**
 * Actualiza una sección existente
 * @param {number} sectionId - ID de la sección
 * @param {Object} sectionData - Datos de la sección a actualizar
 */
export const updateSection = async (sectionId, sectionData) => {
  try {
    ensureAuthenticated();
    
    // Si hay archivos, usar FormData
    if (sectionData.documents && sectionData.documents.length > 0) {
      const formData = new FormData();
      formData.append('title', sectionData.title);
      formData.append('description', sectionData.description);
      formData.append('service_id', sectionData.service_id);
      formData.append('user_id', sectionData.user_id);
      
      // Agregar URLs como array
      if (sectionData.urls && sectionData.urls.length > 0) {
        sectionData.urls.forEach((url, index) => {
          if (url.trim()) {
            formData.append(`urls[${index}]`, url);
          }
        });
      }
      
      // Agregar documentos
      sectionData.documents.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });

      const response = await fetch(`${API_URL}/sections/${sectionId}/update`, {
        method: 'POST',
        headers: getFileUploadHeaders(),
        body: formData
      });
      return await handleApiResponse(response);
    } else {
      // Si no hay archivos, usar JSON
      const response = await fetch(`${API_URL}/sections/${sectionId}/update`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(sectionData)
      });
      return await handleApiResponse(response);
    }
  } catch (error) {
    console.error('Error updating section:', error);
    throw error;
  }
};

/**
 * Elimina una sección (soft delete o permanente)
 * @param {number} sectionId - ID de la sección
 * @param {boolean} force - true para eliminación permanente, false para soft delete
 */
export const deleteSection = async (sectionId, force = false) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/sections/${sectionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ force: force })
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error deleting section:', error);
    throw error;
  }
};

/**
 * Restaura una sección que fue eliminada (soft delete)
 * @param {number} sectionId - ID de la sección a restaurar
 */
export const restoreSection = async (sectionId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/sections/${sectionId}/restore`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error restoring section:', error);
    throw error;
  }
};

// =================== ARCHIVOS/DOCUMENTOS ===================

/**
 * Elimina archivos del servidor
 * @param {string[]} filePaths - Array de rutas de archivos a eliminar
 */
export const deleteFiles = async (filePaths) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/documents/deleteFiles`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ file_paths: filePaths })
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error deleting files:', error);
    throw error;
  }
};

/**
 * Sube múltiples archivos
 * @param {File[]} files - Array de archivos a subir
 * @param {string} type - Tipo de archivos (documents, images, etc.)
 */
export const uploadFiles = async (files, type = 'documents') => {
  try {
    ensureAuthenticated();
    
    const formData = new FormData();
    formData.append('type', type);
    
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });

    const response = await fetch(`${API_URL}/documents/upload`, {
      method: 'POST',
      headers: getFileUploadHeaders(),
      body: formData
    });
    
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error uploading files:', error);
    throw error;
  }
};

// =================== SUBSECCIONES ===================

/**
 * Obtiene todas las subsecciones de una sección
 * @param {number} sectionId - ID de la sección
 */
export const getSectionSubsections = async (sectionId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/sections/${sectionId}/subsections`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting section subsections:', error);
    throw error;
  }
};

/**
 * Obtiene una subsección específica por ID (para administración)
 * @param {number} subsectionId - ID de la subsección
 */
export const getSubsectionById = async (subsectionId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/subsections/${subsectionId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting subsection by ID:', error);
    throw error;
  }
};

/**
 * Crea una nueva subsección
 * @param {Object} subsectionData - Datos de la subsección
 */
export const createSubsection = async (subsectionData) => {
  try {
    ensureAuthenticated();
    
    console.log('Datos enviados al crear subsección:', subsectionData);
    
    // Si hay archivos, usar FormData
    if (subsectionData.documents && subsectionData.documents.length > 0) {
      const formData = new FormData();
      formData.append('title', subsectionData.title);
      formData.append('description', subsectionData.description);
      formData.append('section_id', subsectionData.section_id);
      formData.append('user_id', subsectionData.user_id);
      
      // Agregar URLs como array
      if (subsectionData.urls && subsectionData.urls.length > 0) {
        subsectionData.urls.forEach((url, index) => {
          if (url.trim()) {
            formData.append(`urls[${index}]`, url);
          }
        });
      }
      
      // Agregar documentos
      subsectionData.documents.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });

      const response = await fetch(`${API_URL}/subsections`, {
        method: 'POST',
        headers: getFileUploadHeaders(),
        body: formData
      });
      
      return await handleApiResponse(response);
    } else {
      // Si no hay archivos, usar JSON
      const response = await fetch(`${API_URL}/subsections`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(subsectionData)
      });
      
      return await handleApiResponse(response);
    }
  } catch (error) {
    console.error('Error creating subsection:', error);
    throw error;
  }
};

/**
 * Actualiza una subsección existente
 * @param {number} subsectionId - ID de la subsección
 * @param {Object} subsectionData - Datos de la subsección a actualizar
 */
export const updateSubsection = async (subsectionId, subsectionData) => {
  try {
    ensureAuthenticated();
    
    // Si hay archivos, usar FormData
    if (subsectionData.documents && subsectionData.documents.length > 0) {
      const formData = new FormData();
      formData.append('title', subsectionData.title);
      formData.append('description', subsectionData.description);
      formData.append('section_id', subsectionData.section_id);
      formData.append('user_id', subsectionData.user_id);
      
      // Agregar URLs como array
      if (subsectionData.urls && subsectionData.urls.length > 0) {
        subsectionData.urls.forEach((url, index) => {
          if (url.trim()) {
            formData.append(`urls[${index}]`, url);
          }
        });
      }
      
      // Agregar documentos
      subsectionData.documents.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });

      const response = await fetch(`${API_URL}/subsections/${subsectionId}/update`, {
        method: 'POST',
        headers: getFileUploadHeaders(),
        body: formData
      });
      return await handleApiResponse(response);
    } else {
      // Si no hay archivos, usar JSON
      const response = await fetch(`${API_URL}/subsections/${subsectionId}/update`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(subsectionData)
      });
      return await handleApiResponse(response);
    }
  } catch (error) {
    console.error('Error updating subsection:', error);
    throw error;
  }
};

/**
 * Elimina una subsección (soft delete o permanente)
 * @param {number} subsectionId - ID de la subsección
 * @param {boolean} force - true para eliminación permanente, false para soft delete
 */
export const deleteSubsection = async (subsectionId, force = false) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/subsections/${subsectionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ force: force })
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error deleting subsection:', error);
    throw error;
  }
};

/**
 * Restaura una subsección que fue eliminada (soft delete)
 * @param {number} subsectionId - ID de la subsección a restaurar
 */
export const restoreSubsection = async (subsectionId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/subsections/${subsectionId}/restore`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error restoring subsection:', error);
    throw error;
  }
};

// =================== USUARIOS Y PERMISOS ===================

/**
 * Obtiene todos los usuarios del sistema
 */
export const getAllUsers = async () => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/users`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

/**
 * Obtiene un usuario específico por ID
 * @param {number} userId - ID del usuario
 */
export const getUserById = async (userId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/users/${userId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting user by ID:', error);
    throw error;
  }
};

/**
 * Actualiza los permisos de un usuario
 * @param {number} userId - ID del usuario
 * @param {Object} permissions - Nuevos permisos del usuario
 */
export const updateUserPermissions = async (userId, permissions) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/users/${userId}/permissions`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ permissions })
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error updating user permissions:', error);
    throw error;
  }
};

// =================== ESTADÍSTICAS Y REPORTES ===================

/**
 * Obtiene estadísticas generales del sistema
 */
export const getSystemStats = async () => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/stats`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting system stats:', error);
    throw error;
  }
};

/**
 * Obtiene estadísticas de un servicio específico
 * @param {number} serviceId - ID del servicio
 */
export const getServiceStats = async (serviceId) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/services/${serviceId}/stats`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting service stats:', error);
    throw error;
  }
};

// =================== CONFIGURACIÓN DEL SISTEMA ===================

/**
 * Obtiene la configuración del sistema
 */
export const getSystemConfig = async () => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/config`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error getting system config:', error);
    throw error;
  }
};

/**
 * Actualiza la configuración del sistema
 * @param {Object} config - Nueva configuración
 */
export const updateSystemConfig = async (config) => {
  try {
    ensureAuthenticated();
    
    const response = await fetch(`${API_URL}/admin/config`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(config)
    });
    return await handleApiResponse(response);
  } catch (error) {
    console.error('Error updating system config:', error);
    throw error;
  }
};